import React from 'react';
import { SearchResultRow } from './search-result-row';
import { SearchResultRowTitle } from './search-result-row-title';
import { SearchResultRowSubcontent } from './search-result-row-subcontent';
import { StaffingRequestState } from 'works/components/staffing-request-state';
import { ApplicationStateBadge } from 'works/components/application-state-badge';
import { StaffingRequestAssignmentState } from 'works/components/staffing-request-assignment-state';
import Auth from 'works/legacy/lib/auth';
import { WorksAutocompleteSearchResponse } from '@institutions/graphql';

type Results = WorksAutocompleteSearchResponse['worksAutocompleteSearch'];

const renderApplicationOptions = (
  applications: Results['staffingRequestApplications']
) => {
  if (!applications?.length) {
    return renderEmptyLabel('No submissions found');
  }

  return applications.map((app) => {
    const appUrl = `/works/applications/${app.id}`;

    return {
      key: app.id,
      redirectToUrl: appUrl,
      value: `${app.worksProfile.fullName} - ${app.staffingRequest.code}`,
      label: (
        <SearchResultRow>
          {app.worksProfile.fullName} - {app.staffingRequest.code}
          <SearchResultRowSubcontent>
            <ApplicationStateBadge state={app.state} />
          </SearchResultRowSubcontent>
        </SearchResultRow>
      ),
    };
  });
};

const buildOrgAssignUrl = (
  assignment: NonNullable<Results['staffingRequestAssignments']>[number]
) => {
  const url = ['works'];

  url.push('team');

  if (assignment.worker !== null) {
    url.push(assignment.staffingRequestType);
    url.push(assignment.worker.id);
    url.push('assignments');
    url.push(assignment.id);
    url.push('assignment');
  } else {
    // TODO: improve this behavior https://app.asana.com/1/303134204339906/project/1210244193526850/task/1211610043557715?focus=true
    // if worker doesn't exist the long_term URL is the only one that works
    url.push('long_term');
    url.push(assignment.id);
  }

  return `/${url.join('/')}`;
};

const buildSupplierAssignUrl = (
  assignment: NonNullable<Results['staffingRequestAssignments']>[number]
) => `/works/team/${assignment.id}`;

const renderAssignmentOptions = (
  assignments: Results['staffingRequestAssignments'],
  isOrg: boolean
) => {
  if (!assignments?.length) {
    return renderEmptyLabel('No assignments found');
  }

  return assignments.map((assign) => {
    const assignUrl = isOrg
      ? buildOrgAssignUrl(assign)
      : buildSupplierAssignUrl(assign);

    return {
      key: assign.id,
      redirectToUrl: assignUrl,
      value: `${assign.worksProfile.fullName} (${assign.staffingRequest.code})`,
      label: (
        <SearchResultRow>
          {assign.worksProfile.fullName} ({assign.staffingRequest.code})
          <SearchResultRowSubcontent>
            <StaffingRequestAssignmentState state={assign.state} />
          </SearchResultRowSubcontent>
        </SearchResultRow>
      ),
    };
  });
};

const renderWorkerOptions = (workers: Results['workers']) => {
  if (!workers?.length) {
    return renderEmptyLabel('No on demand workers found');
  }

  return workers.map((worker) => {
    const workerType = worker.isInternal ? 'Internal' : 'External';
    const workerUrl = `/works/team/on_demand/${worker.id}`;

    return {
      key: worker.id,
      redirectToUrl: workerUrl,
      value: `${worker.worksUser.name} - ${workerType}`,
      label: (
        <SearchResultRow>
          {worker.worksUser.name} - {workerType}
        </SearchResultRow>
      ),
    };
  });
};

const renderProfileOptions = (profiles: Results['worksProfiles']) => {
  if (!profiles?.length) {
    return renderEmptyLabel('No candidates found');
  }

  return profiles.map((profile) => {
    const profileUrl = `/works/supplier/candidates/${profile.id}`;

    return {
      key: profile.id,
      redirectToUrl: profileUrl,
      value: profile.fullName,
      label: (
        <SearchResultRow>
          {profile.fullName}
          <SearchResultRowSubcontent>{profile.email}</SearchResultRowSubcontent>
        </SearchResultRow>
      ),
    };
  });
};

const renderRequestOptions = (requests: Results['staffingRequests']) => {
  if (!requests?.length) {
    return renderEmptyLabel('No requests found');
  }

  return requests.map((request) => {
    const requestUrl = Auth.organizationMember()
      ? `/works/staffing_requests/${request.id}`
      : `/works/supplier/staffing_requests/${request.id}`;

    return {
      key: request.id,
      redirectToUrl: requestUrl,
      value: `${request.code} - ${request.name}`,
      label: (
        <SearchResultRow>
          {request.code} - {request.name}
          <SearchResultRowSubcontent>
            <StaffingRequestState state={request.state} />
          </SearchResultRowSubcontent>
        </SearchResultRow>
      ),
    };
  });
};

const renderEmptyLabel = (title: string) => [
  {
    value: title,
    label: title,
    disabled: 'disabled' as const,
    options: [],
  },
];

export const renderSearchResults = (results: Results) => {
  const formattedResults: {
    label: React.ReactNode;
    options: {
      key?: string;
      redirectToUrl?: string;
      value: string;
      label: React.ReactNode | string;
      disabled?: 'disabled';
    }[];
  }[] = [];

  if (results.canShowStaffingRequests.value) {
    formattedResults.push({
      label: <SearchResultRowTitle title="Requests" />,
      options: renderRequestOptions(results.staffingRequests),
    });
  }

  if (results.canShowStaffingRequestApplications.value) {
    formattedResults.push({
      label: <SearchResultRowTitle title="Submissions" />,
      options: renderApplicationOptions(results.staffingRequestApplications),
    });
  }

  if (results.canShowWorksProfiles.value) {
    formattedResults.push({
      label: <SearchResultRowTitle title="Candidates" />,
      options: renderProfileOptions(results.worksProfiles),
    });
  }

  if (results.canShowStaffingRequestAssignments.value) {
    formattedResults.push({
      label: <SearchResultRowTitle title="Assignments" />,
      options: renderAssignmentOptions(
        results.staffingRequestAssignments,
        results.isOrg
      ),
    });
  }

  if (results.canShowWorkers.value) {
    formattedResults.push({
      label: <SearchResultRowTitle title="Workers" />,
      options: renderWorkerOptions(results.workers),
    });
  }

  return formattedResults.length === 0
    ? renderEmptyLabel('Nothing found')
    : formattedResults;
};
